import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkle, Download, ArrowSquareOut, Lock, CheckCircle, X, Lightning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { prototypeAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export default function PrototypeViewerPage() {
  const { prototypeId } = useParams();
  const { user } = useAuth();
  const [proto, setProto] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollStartedAt = useRef(0);

  useEffect(() => {
    let timer;
    let mounted = true;

    const fetchOnce = async () => {
      try {
        const res = await prototypeAPI.get(prototypeId);
        return res.data;
      } catch (err) {
        return null;
      }
    };

    const poll = async () => {
      const data = await fetchOnce();
      if (!mounted) return;
      if (data) setProto(data);
      setLoading(false);
      if (!data) return;
      if (data.status === 'ready' || data.status === 'failed') return;
      if (Date.now() - pollStartedAt.current > MAX_POLL_DURATION_MS) return;
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    pollStartedAt.current = Date.now();
    poll();
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [prototypeId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <div className="w-10 h-10 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--primary-accent)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!proto) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-heading text-3xl font-bold mb-4">Prototype not found</h1>
          <Link to="/start-project">
            <Button style={{ background: 'var(--primary-accent)', color: '#fff' }}>Generate a new proposal →</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const isReady = proto.status === 'ready';
  const isFailed = proto.status === 'failed';
  const previewUrl = isReady ? `${backendUrl}${proto.preview_path}` : null;

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO
        path={`/prototype-preview/${prototypeId}`}
        title={isReady ? proto.title : 'Generating prototype'}
        description={proto.tagline || 'AI-generated interactive prototype by XceedAgents.ai'}
      />
      <Navbar />

      <section className="border-b" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-2" style={{ color: 'var(--primary-accent)' }}>
                <Sparkle weight="fill" size={12} />
                {proto.is_teaser ? 'Free Teaser Preview' : 'Full Interactive Prototype'}
              </div>
              <h1 className="font-heading text-2xl lg:text-3xl font-bold" style={{ color: 'var(--text-primary)' }} data-testid="proto-title">
                {proto.title}
              </h1>
              {proto.tagline && (
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{proto.tagline}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {isReady && (
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="gap-1.5" data-testid="proto-open-new-tab">
                    <ArrowSquareOut size={14} /> Open in new tab
                  </Button>
                </a>
              )}
              {proto.is_teaser && proto.lead_id && (
                user ? (
                  <Link to={`/proposal/${proto.lead_id}?unlock=1`}>
                    <Button className="gap-1.5" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="proto-unlock-full">
                      <Lightning weight="fill" size={14} /> Unlock Full Prototype
                    </Button>
                  </Link>
                ) : (
                  <Link to={`/login?next=/proposal/${proto.lead_id}`}>
                    <Button className="gap-1.5" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="proto-signup-unlock">
                      <Lock weight="fill" size={14} /> Sign in to Unlock Full
                    </Button>
                  </Link>
                )
              )}
            </div>
          </div>

          {/* Pages list */}
          {isReady && proto.pages?.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Pages:</span>
              {proto.pages.map((p, i) => (
                <a
                  key={i}
                  href={`${backendUrl}/api/prototype-files/${proto.id}/${p.slug === 'index' ? 'index.html' : p.slug + '.html'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-2.5 py-1 rounded-md font-medium hover:underline"
                  style={{ background: 'var(--tag-bg)', color: 'var(--primary-accent)' }}
                  data-testid={`proto-page-${p.slug}`}
                >
                  {p.name}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Iframe preview */}
      <section className="bg-zinc-100 py-6" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6">
          {isReady ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="rounded-xl overflow-hidden border-2 bg-white shadow-elevated"
              style={{ borderColor: 'var(--border-strong)' }}
            >
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ background: '#F1F5F9', borderColor: 'var(--border)' }}>
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 mx-3 px-3 py-1 rounded text-xs font-mono truncate" style={{ background: '#FFFFFF', color: 'var(--text-muted)' }}>
                  {previewUrl}
                </div>
              </div>
              <iframe
                src={previewUrl}
                title={proto.title}
                className="w-full bg-white"
                style={{ height: '78vh', minHeight: '650px' }}
                sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
                data-testid="proto-iframe"
              />
            </motion.div>
          ) : isFailed ? (
            <div className="p-10 rounded-xl border bg-white text-center" style={{ borderColor: 'var(--error)' }} data-testid="proto-failed">
              <X size={32} weight="bold" style={{ color: 'var(--error)' }} className="mx-auto mb-3" />
              <h3 className="font-heading text-xl font-bold mb-2">Generation failed</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{proto.error || 'Something went wrong. Please retry from the proposal page.'}</p>
              {proto.lead_id && (
                <Link to={`/proposal/${proto.lead_id}`}>
                  <Button style={{ background: 'var(--primary-accent)', color: '#fff' }}>Back to proposal →</Button>
                </Link>
              )}
            </div>
          ) : (
            <GeneratingState isTeaser={proto.is_teaser} />
          )}
        </div>
      </section>

      {/* Footer CTA */}
      {isReady && proto.is_teaser && (
        <section className="py-16" style={{ background: 'var(--dark-section-bg)' }}>
          <div className="max-w-4xl mx-auto px-6 text-center" style={{ color: 'var(--dark-section-text)' }}>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-3">Like what you see? Unlock the full prototype.</h2>
            <p className="text-base mb-7" style={{ color: 'var(--dark-section-muted)' }}>
              The full prototype includes 5-7 working pages with navigation, forms, dashboards, and realistic data — built to match your scope. <strong style={{ color: '#FB923C' }}>150 credits</strong> (you have {user?.credits ?? 100} free credits on signup).
            </p>
            {user ? (
              <Link to={`/proposal/${proto.lead_id}?unlock=1`}>
                <Button className="h-12 px-8 font-semibold gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }}>
                  Unlock Full Prototype <ArrowRight weight="bold" />
                </Button>
              </Link>
            ) : (
              <Link to={`/login?next=/proposal/${proto.lead_id}`}>
                <Button className="h-12 px-8 font-semibold gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }}>
                  Sign Up to Unlock (100 free credits) <ArrowRight weight="bold" />
                </Button>
              </Link>
            )}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

function GeneratingState({ isTeaser }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const messages = isTeaser ? [
    'Reading your requirement…',
    'Designing the layout…',
    'Generating HTML, CSS, JS…',
    'Polishing the visuals…',
    'Almost ready!',
  ] : [
    'Analyzing your requirements…',
    'Architecting page structure…',
    'Designing the visual system…',
    'Generating HTML for each page…',
    'Wiring up interactions and forms…',
    'Adding realistic mock data…',
    'Finalizing your prototype…',
  ];
  const msgIdx = Math.min(Math.floor(elapsed / (isTeaser ? 12 : 18)), messages.length - 1);

  return (
    <div className="p-12 rounded-xl border-2 bg-white text-center" style={{ borderColor: 'var(--border-strong)' }} data-testid="proto-generating">
      <div className="inline-block w-12 h-12 border-2 rounded-full animate-spin mb-5" style={{ borderColor: 'var(--primary-accent)', borderTopColor: 'transparent' }} />
      <h3 className="font-heading text-2xl font-bold mb-2">{messages[msgIdx]}</h3>
      <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
        Claude Sonnet 4.5 is building a working prototype tailored to your scope.
      </p>
      <p className="text-xs mt-3 font-mono" style={{ color: 'var(--text-muted)' }}>
        {elapsed}s elapsed · {isTeaser ? 'usually 45-90s' : 'usually 90-180s'}
      </p>
    </div>
  );
}
